import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  Bell,
  CheckCircle2,
  Database,
  FileText,
  GitCompare,
  LineChart,
  Shield,
  Clock,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBranch } from '../../contexts/BranchContext';
import { marketPulseApi } from '../../services/apiService';
import MarketPulseHeroWave from './MarketPulseHeroWave';
import MarketPulseProductDocModal from './MarketPulseProductDocModal';
import {
  MARKET_PULSE_BENEFIT_CARDS,
  MARKET_PULSE_COMING_SOON,
  MARKET_PULSE_SOCIAL_STATS,
  MARKET_PULSE_STORY_FLOW,
  MARKET_PULSE_STORY_STEPS,
  MARKET_PULSE_TRUST_PRINCIPLES,
  MARKET_PULSE_WAITLIST_STORAGE_KEY,
} from './marketPulseComingSoonData';
import '../../styles/marketPulseLanding.css';

type WaitlistStatus = 'idle' | 'submitting' | 'success' | 'already_registered' | 'error';

const BENEFIT_ICONS = {
  'trending-up': TrendingUp,
  'bar-chart-3': BarChart3,
  'line-chart': LineChart,
  shield: Shield,
} as const;

const TRUST_ICONS = {
  shield: Shield,
  'git-compare': GitCompare,
} as const;

const STORY_ICONS = {
  database: Database,
  'git-compare': GitCompare,
  target: Target,
} as const;

function isStoryFlowNodeActive(stepIndex: number, nodeIndex: number): boolean {
  if (stepIndex === 0) return nodeIndex <= 1;
  if (stepIndex === 1) return nodeIndex === 2;
  return nodeIndex === 3;
}

function isStoryFlowNodePast(stepIndex: number, nodeIndex: number): boolean {
  if (stepIndex === 1) return nodeIndex <= 1;
  if (stepIndex === 2) return nodeIndex <= 2;
  return false;
}

function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function useScrollProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const max = scrollHeight - clientHeight;
      setProgress(max > 0 ? scrollTop / max : 0);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return progress;
}

interface WaitlistCtaProps {
  email: string;
  showEmailField: boolean;
  status: WaitlistStatus;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  variant?: 'hero' | 'final';
}

const WaitlistCta: React.FC<WaitlistCtaProps> = ({
  email,
  showEmailField,
  status,
  onEmailChange,
  onSubmit,
  variant = 'hero',
}) => {
  const isHero = variant === 'hero';
  const isRegistered = status === 'success' || status === 'already_registered';
  const statusMessage =
    status === 'success'
      ? MARKET_PULSE_COMING_SOON.ctaSuccess
      : status === 'already_registered'
        ? MARKET_PULSE_COMING_SOON.ctaAlready
        : status === 'error'
          ? MARKET_PULSE_COMING_SOON.ctaError
          : null;

  return (
    <div className={isHero ? 'max-w-lg mx-auto' : 'max-w-md mx-auto'}>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-center">
        {showEmailField && !isRegistered && (
          <label className="flex-1 text-right">
            <span
              className={`block text-xs mb-1.5 mp-hero-email-label ${
                isHero ? '' : 'text-text-secondary'
              }`}
            >
              {MARKET_PULSE_COMING_SOON.emailLabel}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder={MARKET_PULSE_COMING_SOON.emailPlaceholder}
              className={
                isHero
                  ? 'mp-hero-email-input w-full rounded-xl border border-white/20 bg-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[rgba(203,255,0,0.45)]'
                  : 'w-full rounded-xl border border-border-primary bg-bg-primary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-[var(--mp-accent)]/40'
              }
            />
          </label>
        )}
        <button
          type="button"
          onClick={onSubmit}
          disabled={status === 'submitting' || isRegistered}
          className={isHero ? 'mp-hero-cta-primary' : 'mp-cta-final-btn'}
        >
          {isRegistered ? (
            <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
          ) : (
            <Bell className="w-4 h-4" aria-hidden="true" />
          )}
          {status === 'submitting'
            ? MARKET_PULSE_COMING_SOON.ctaSubmitting
            : isRegistered
              ? 'ثبت شد'
              : isHero
                ? MARKET_PULSE_COMING_SOON.cta
                : MARKET_PULSE_COMING_SOON.ctaFinal}
        </button>
      </div>
      {statusMessage && (
        <p
          className={`mt-3 text-sm font-medium text-center ${isHero ? 'text-emerald-200' : 'text-emerald-600'}`}
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </p>
      )}
    </div>
  );
};

const MarketPulseComingSoon: React.FC = () => {
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [status, setStatus] = useState<WaitlistStatus>('idle');
  const [email, setEmail] = useState(user?.email ?? '');
  const [showEmailField, setShowEmailField] = useState(false);
  const [isProductDocOpen, setIsProductDocOpen] = useState(false);
  const [activeStoryStep, setActiveStoryStep] = useState(0);

  const scrollProgress = useScrollProgress();
  const socialReveal = useScrollReveal<HTMLElement>();
  const benefitsReveal = useScrollReveal<HTMLElement>();
  const storyReveal = useScrollReveal<HTMLElement>();
  const trustReveal = useScrollReveal<HTMLElement>();
  const finalReveal = useScrollReveal<HTMLElement>();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(MARKET_PULSE_WAITLIST_STORAGE_KEY) === '1') {
      setStatus('already_registered');
    }
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);

  const resolvedEmail = useMemo(() => email.trim(), [email]);

  const handleJoinWaitlist = useCallback(async () => {
    if (status === 'submitting' || status === 'success' || status === 'already_registered') return;
    if (!resolvedEmail) {
      setShowEmailField(true);
      return;
    }

    setStatus('submitting');
    try {
      await marketPulseApi.joinWaitlist({
        email: resolvedEmail,
        mobile: (user as { mobile?: string } | null)?.mobile,
        brandId: selectedBranch?.brandId,
        branchId: selectedBranch?.id,
        brandName: undefined,
        branchName: selectedBranch?.name,
        userName: user?.name,
      });
      localStorage.setItem(MARKET_PULSE_WAITLIST_STORAGE_KEY, '1');
      setStatus('success');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 409) {
        localStorage.setItem(MARKET_PULSE_WAITLIST_STORAGE_KEY, '1');
        setStatus('already_registered');
      } else {
        setStatus('error');
      }
    }
  }, [resolvedEmail, selectedBranch, status, user]);

  return (
    <>
      <div className="mp-progress-bar" aria-hidden="true">
        <div className="mp-progress-bar-fill" style={{ transform: `scaleX(${scrollProgress})` }} />
      </div>

      <main
        dir="rtl"
        className="mp-landing"
        data-mp-bg="brand"
        aria-labelledby="mp-landing-title"
      >
        <section className="mp-hero mp-hero--cinematic">
          <MarketPulseHeroWave />
          <div className="mp-hero-inner container-dashboard py-14 sm:py-20 lg:py-24">
            <div className="mp-hero-content max-w-2xl mx-auto text-center">
              <h1 id="mp-landing-title" className="mp-hero-product-name">
                {MARKET_PULSE_COMING_SOON.title}
              </h1>

              <span className="mp-hero-status-badge" role="status">
                <Clock className="mp-hero-status-badge__icon" aria-hidden="true" />
                {MARKET_PULSE_COMING_SOON.badge}
              </span>

              <h2 className="mp-hero-headline">
                {MARKET_PULSE_COMING_SOON.headline}
              </h2>

              <p className="mp-hero-subtitle mx-auto">
                {MARKET_PULSE_COMING_SOON.subheadline}
              </p>

              <div className="mp-hero-cta-block">
                <WaitlistCta
                  email={email}
                  showEmailField={showEmailField}
                  status={status}
                  onEmailChange={setEmail}
                  onSubmit={handleJoinWaitlist}
                  variant="hero"
                />
                <button
                  type="button"
                  onClick={() => setIsProductDocOpen(true)}
                  className="mp-product-doc-link mp-product-doc-link--ghost"
                >
                  <FileText className="w-4 h-4" aria-hidden="true" />
                  {MARKET_PULSE_COMING_SOON.productDocCta}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section
          ref={socialReveal.ref}
          className={`mp-section mp-section-stats border-b border-border-primary/60 mp-reveal ${socialReveal.visible ? 'mp-visible' : ''}`}
          aria-label={MARKET_PULSE_COMING_SOON.highlightsTitle}
        >
          <div className="container-dashboard">
            <p className="text-center text-xs font-semibold text-text-secondary mb-8 tracking-wide">
              {MARKET_PULSE_COMING_SOON.highlightsTitle}
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {MARKET_PULSE_SOCIAL_STATS.map((stat) => (
                <div
                  key={stat.label}
                  className="mp-glass-light rounded-2xl p-4 sm:p-5 text-center transition-colors"
                >
                  <p className="text-2xl sm:text-3xl font-black mp-accent-text tabular-nums">{stat.value}</p>
                  <p className="text-sm font-bold text-text-primary mt-1">{stat.label}</p>
                  <p className="text-[10px] sm:text-xs text-text-secondary mt-1 leading-relaxed">{stat.suffix}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          ref={benefitsReveal.ref}
          className={`mp-section mp-reveal ${benefitsReveal.visible ? 'mp-visible' : ''}`}
          aria-labelledby="mp-benefits-heading"
        >
          <div className="container-dashboard max-w-5xl">
            <h2 id="mp-benefits-heading" className="text-xl sm:text-2xl font-black text-text-primary text-center mb-8">
              {MARKET_PULSE_COMING_SOON.benefitsTitle}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {MARKET_PULSE_BENEFIT_CARDS.map((card) => {
                const Icon = BENEFIT_ICONS[card.icon];
                return (
                  <article
                    key={card.id}
                    className={`mp-benefit-card rounded-2xl border border-border-primary bg-gradient-to-br ${card.accent} p-5 sm:p-6 text-center`}
                  >
                    <div className="mp-benefit-icon">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h3 className="text-base font-bold text-text-primary mb-2">{card.title}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{card.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section
          ref={storyReveal.ref}
          className={`mp-section bg-bg-secondary/50 mp-reveal ${storyReveal.visible ? 'mp-visible' : ''}`}
          aria-labelledby="mp-story-heading"
        >
          <div className="container-dashboard max-w-3xl">
            <h2 id="mp-story-heading" className="text-xl sm:text-2xl font-black text-text-primary text-center mb-8">
              {MARKET_PULSE_COMING_SOON.storyTitle}
            </h2>

            <div
              className="mp-story-flow mb-8"
              aria-hidden="true"
            >
              {MARKET_PULSE_STORY_FLOW.map((node, nodeIndex) => (
                <React.Fragment key={node.id}>
                  <div
                    className={`mp-story-flow-node ${
                      isStoryFlowNodeActive(activeStoryStep, nodeIndex)
                        ? 'mp-story-flow-node-active'
                        : isStoryFlowNodePast(activeStoryStep, nodeIndex)
                          ? 'mp-story-flow-node-past'
                          : ''
                    }`}
                  >
                    {node.label}
                  </div>
                  {nodeIndex < MARKET_PULSE_STORY_FLOW.length - 1 && (
                    <div
                      className={`mp-story-flow-connector ${
                        isStoryFlowNodePast(activeStoryStep, nodeIndex + 1) ||
                        isStoryFlowNodeActive(activeStoryStep, nodeIndex + 1)
                          ? 'mp-story-flow-connector-active'
                          : ''
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div
              role="tablist"
              aria-label={MARKET_PULSE_COMING_SOON.storyTitle}
              className="mp-story-tabs mb-6"
            >
              {MARKET_PULSE_STORY_STEPS.map((step, i) => (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  id={`mp-story-tab-${step.id}`}
                  aria-selected={activeStoryStep === i}
                  aria-controls={`mp-story-panel-${step.id}`}
                  onClick={() => setActiveStoryStep(i)}
                  className={`mp-story-tab ${activeStoryStep === i ? 'mp-story-tab-active' : ''}`}
                >
                  <span className="mp-story-tab-step">{step.step}</span>
                  <span className="mp-story-tab-label">{step.tabLabel}</span>
                </button>
              ))}
            </div>

            {MARKET_PULSE_STORY_STEPS.map((step, i) => {
              if (activeStoryStep !== i) return null;
              const Icon = STORY_ICONS[step.icon];
              return (
                <article
                  key={step.id}
                  role="tabpanel"
                  id={`mp-story-panel-${step.id}`}
                  aria-labelledby={`mp-story-tab-${step.id}`}
                  className="mp-story-panel mp-story-panel-enter"
                >
                  <div className="mp-story-panel-icon">
                    <Icon className="w-7 h-7" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-2">{step.title}</h3>
                  <p className="text-sm text-text-secondary leading-relaxed mb-5">{step.description}</p>
                  <ul className="mp-story-details">
                    {step.details.map((detail) => (
                      <li key={detail} className="mp-story-detail-item">
                        <CheckCircle2 className="w-4 h-4 shrink-0 mp-accent-text" aria-hidden="true" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <section
          ref={trustReveal.ref}
          className={`mp-section mp-reveal ${trustReveal.visible ? 'mp-visible' : ''}`}
          aria-labelledby="mp-trust-heading"
        >
          <div className="container-dashboard max-w-3xl">
            <h2 id="mp-trust-heading" className="text-xl sm:text-2xl font-black text-text-primary text-center mb-10">
              {MARKET_PULSE_COMING_SOON.trustTitle}
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {MARKET_PULSE_TRUST_PRINCIPLES.map((principle) => {
                const Icon = TRUST_ICONS[principle.icon];
                return (
                  <article key={principle.id} className="mp-glass-light mp-trust-card rounded-2xl p-5 sm:p-6">
                    <div className="mp-trust-icon">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h3 className="text-base font-bold text-text-primary mb-2">{principle.title}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{principle.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section
          ref={finalReveal.ref}
          className={`mp-section pb-16 mp-reveal ${finalReveal.visible ? 'mp-visible' : ''}`}
          aria-labelledby="mp-final-cta-heading"
        >
          <div className="container-dashboard max-w-2xl">
            <div className="mp-final-cta">
              <div className="relative z-10">
                <h2 id="mp-final-cta-heading" className="text-2xl sm:text-3xl font-black mb-3">
                  {MARKET_PULSE_COMING_SOON.ctaFinal}
                </h2>
                <p className="mp-final-cta-note">{MARKET_PULSE_COMING_SOON.privacyNote}</p>
                <WaitlistCta
                  email={email}
                  showEmailField={showEmailField}
                  status={status}
                  onEmailChange={setEmail}
                  onSubmit={handleJoinWaitlist}
                  variant="final"
                />
              </div>
            </div>
          </div>
        </section>

      </main>

      <MarketPulseProductDocModal isOpen={isProductDocOpen} onClose={() => setIsProductDocOpen(false)} />
    </>
  );
};

export default MarketPulseComingSoon;
